import { downloadApiFile, req } from "./transport";
import type { EmployerApplication, EmployerJob, JobOffer, JobOfferInput, TalentCandidate } from "./types";

export const employmentApi = {
  // Increment 30: job applications
  async applyToJob(jobId: string, coverNote?: string) {
    return req<{ application: any }>(`/jobs/${encodeURIComponent(jobId)}/apply`, { method: "POST", body: JSON.stringify({ coverNote }) });
  },

  async withdrawApplication(jobId: string) {
    return req<{ application: any }>(`/jobs/${encodeURIComponent(jobId)}/withdraw`, { method: "POST" });
  },

  async myJobApplication(jobId: string) {
    const r = await req<{ application: any | null }>(`/jobs/${encodeURIComponent(jobId)}/application/me`, {}, { rejectUnauthorized: true });
    return r?.application ?? null;
  },

  async jobApplicants(jobId: string) {
    return req<{ job: any; applications: any[] }>(`/jobs/${encodeURIComponent(jobId)}/applications`);
  },

  // v3.9.55 (Stage 3 / E1-E2) — employer hiring pipeline (all applicants across the employer's jobs)
  async employerApplications(signal?: AbortSignal) {
    return req<{ jobs: EmployerJob[]; applications: EmployerApplication[] }>("/applications/employer", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async downloadApplicationResume(appId: string) {
    return downloadApiFile(`/applications/${encodeURIComponent(appId)}/resume`, `resume-${appId}`,
      "Could not download the applicant's résumé.");
  },

  async setApplicationStatus(appId: string, status: "shortlisted" | "accepted" | "rejected") {
    return req<{ application: any; jobClosed?: boolean }>(`/applications/${encodeURIComponent(appId)}/status`, { method: "POST", body: JSON.stringify({ status }) });
  },

  async sendJobOffer(applicationId: string, input: JobOfferInput) {
    return req<{ offer: JobOffer }>(`/applications/${encodeURIComponent(applicationId)}/offer`, { method: "POST", body: JSON.stringify(input) });
  },

  async jobOffer(id: string) {
    return req<{ offer: JobOffer; currentOffer: JobOffer | null }>(`/job-offers/${encodeURIComponent(id)}`);
  },

  async proposeJobSalary(offerId: string, input: JobOfferInput) {
    return req<{ offer: JobOffer }>(`/job-offers/${encodeURIComponent(offerId)}/propose`, { method: "POST", body: JSON.stringify(input) });
  },

  async decideJobOffer(offerId: string, action: "accept" | "decline" | "withdraw") {
    return req<{ offer: JobOffer }>(`/job-offers/${encodeURIComponent(offerId)}/${action}`, { method: "POST" });
  },

  // v3.9.56 (Stage 3 / D1-D2) — talent pool: saved candidates + invite-to-apply
  async talentPool(signal?: AbortSignal) {
    return req<{ candidates: TalentCandidate[]; openJobs: { id: string; title: string }[] }>("/applications/talent-pool", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async inviteToJob(jobId: string, workerUserId: number) {
    return req<{ ok: boolean }>(`/jobs/${encodeURIComponent(jobId)}/invite`, { method: "POST", body: JSON.stringify({ workerUserId }) });
  },

  // v3.9.57 (Stage 3 / E3) — candidate notes + interview scheduling
  async applicationNotes(appId: string) {
    return req<{ notes: { id: string; body: string; createdAt: string }[] }>(`/applications/${encodeURIComponent(appId)}/notes`);
  },

  async addApplicationNote(appId: string, body: string) {
    return req<{ note: { id: string; body: string; createdAt: string } }>(`/applications/${encodeURIComponent(appId)}/notes`, { method: "POST", body: JSON.stringify({ body }) });
  },

  async scheduleInterview(appId: string, at: string | null, note?: string) {
    return req<{ application: any }>(`/applications/${encodeURIComponent(appId)}/interview`, { method: "POST", body: JSON.stringify({ at, note }) });
  },

  async myApplications(signal?: AbortSignal) {
    // Manual refresh needs cancellation and a real access failure, not empty success.
    const r = await req<{ applications: any[] }>("/applications/me", { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.applications)) throw new Error("Missing applications.");
    return r?.applications ?? [];
  }
};
